export const MAX_FILES = 10

// Must not exceed MAX_UPLOAD_BYTES on the forms service.
export const MAX_ATTACHMENT_BYTES = Number(process.env.REACT_APP_MAX_ATTACHMENT_BYTES ?? 1024 * 1024 * 1024)

const DEFAULT_CONTENT_TYPE = 'application/octet-stream'

export type StoredUpload = { key: string; contentType: string }

export type Attachment = {
    id: string
    file: File
    // Set once the file is in the bucket, so a retry after a failed send does not upload it again.
    stored?: StoredUpload
}

export type PresignedUpload = { key: string; url: string; headers: Record<string, string> }

export class UploadError extends Error {
    constructor(readonly fileName?: string) {
        super(fileName ? `Upload of ${fileName} failed` : 'Uploads are unavailable')
    }
}

export function fileId(file: File): string {
    return `${file.name}\u0000${file.size}\u0000${file.lastModified}`
}

export function totalBytes(attachments: Attachment[]): number {
    return attachments.reduce((total, attachment) => total + attachment.file.size, 0)
}

export function contentTypeOf(file: File): string {
    return file.type || DEFAULT_CONTENT_TYPE
}

export function formatBytes(bytes: number): string {
    const units = ['KB', 'MB', 'GB']
    if (bytes < 1024) return `${bytes} B`

    let value = bytes / 1024
    let unit = 0
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024
        unit += 1
    }
    const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value)
    return `${rounded} ${units[unit]}`
}

type Refusal = { name: string; reason: string }

export function addAttachments(current: Attachment[], picked: File[]): { attachments: Attachment[]; notice: string } {
    const attachments = [...current]
    const refused: Refusal[] = []
    let total = totalBytes(attachments)

    picked.forEach((file) => {
        const id = fileId(file)
        if (attachments.some((attachment) => attachment.id === id)) return

        if (file.size === 0) refused.push({ name: file.name, reason: 'it is empty' })
        else if (attachments.length >= MAX_FILES) refused.push({ name: file.name, reason: `the limit is ${MAX_FILES} files` })
        else if (total + file.size > MAX_ATTACHMENT_BYTES)
            refused.push({ name: file.name, reason: `the limit is ${formatBytes(MAX_ATTACHMENT_BYTES)} in total` })
        else {
            attachments.push({ id, file })
            total += file.size
        }
    })

    const notice = refused.length
        ? `Not attached: ${refused.map(({ name, reason }) => `${name} (${reason})`).join(', ')}.`
        : ''
    return { attachments, notice }
}

function uploadsEndpoint(contactEndpoint: string): string {
    return new URL('uploads', new URL(contactEndpoint, window.location.href)).toString()
}

export async function presign(contactEndpoint: string, files: File[], timeoutMs: number): Promise<PresignedUpload[]> {
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs)

    try {
        const response = await fetch(uploadsEndpoint(contactEndpoint), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                files: files.map((file) => ({ name: file.name, size: file.size, contentType: contentTypeOf(file) })),
            }),
            signal: controller.signal,
        })
        if (!response.ok) throw new UploadError()

        const { uploads } = (await response.json()) as { uploads?: PresignedUpload[] }
        if (!uploads || uploads.length !== files.length) throw new UploadError()
        return uploads
    } catch (error) {
        throw error instanceof UploadError ? error : new UploadError()
    } finally {
        window.clearTimeout(timeout)
    }
}

// An upload the service no longer accepts, most likely expired, has to be uploaded again on the next try.
export async function refusesAttachments(response: Response): Promise<boolean> {
    try {
        const { errors } = (await response.json()) as { errors?: string[] }
        return (errors ?? []).some((error) => error.startsWith('attachment'))
    } catch {
        return false
    }
}

// XMLHttpRequest rather than fetch: fetch cannot report upload progress.
export function putFile(
    upload: PresignedUpload,
    file: File,
    onProgress: (loaded: number) => void,
    track: (request: XMLHttpRequest) => () => void
): Promise<StoredUpload> {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        const untrack = track(request)
        const fail = () => {
            untrack()
            reject(new UploadError(file.name))
        }

        request.open('PUT', upload.url)
        Object.keys(upload.headers).forEach((name) => request.setRequestHeader(name, upload.headers[name]))
        request.upload.onprogress = (event) => onProgress(event.loaded)
        request.onload = () => {
            if (request.status < 200 || request.status >= 300) return fail()
            untrack()
            onProgress(file.size)
            resolve({ key: upload.key, contentType: contentTypeOf(file) })
        }
        request.onerror = fail
        request.onabort = fail
        request.send(file)
    })
}
